import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useOrganization } from '@agion/auth'
import { Alert, Badge, Button, Combobox, type ComboboxOption } from '@agion/ui'
import { RefreshCw, SlidersHorizontal } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { PageHeader } from '@/layouts/InfraPageHeader'
import { rolloutControlsFor } from './rollout_catalog'
import { rolloutErrorText } from './rollout_refusal'
import { listStaffRolloutOrganizations, type StaffRolloutOrganization, type StaffRolloutOrganizationList } from './infra_staff_rollout.service'

type DirectoryState =
  | { status: 'loading' }
  | { status: 'ready'; list: StaffRolloutOrganizationList }
  | { status: 'error'; message: string }

const targetPickerLabelID = 'rollout-target-organization-label'

/**
 * Rollout controls (PLAN-20260909, PLAN-20260930 slice 5). The page defaults
 * to the active organisation; a platform administrator can pick another
 * organisation from the server's staff list. For another organisation only
 * the catalogue entries that declare target support render, and their cards
 * read and write through the staff routes. The server decides every change.
 */
export function InfraRolloutControlsPage() {
  const { t } = useTranslation('pages')
  const { active, isLoading } = useOrganization()
  const [directory, setDirectory] = useState<DirectoryState>({ status: 'loading' })
  const [selectedTargetId, setSelectedTargetId] = useState<string | null>(null)
  const directoryGeneration = useRef(0)

  const loadDirectory = useCallback(async () => {
    const generation = ++directoryGeneration.current
    setDirectory({ status: 'loading' })
    try {
      const list = await listStaffRolloutOrganizations()
      if (generation !== directoryGeneration.current) return
      setDirectory({ status: 'ready', list })
    } catch (error) {
      if (generation !== directoryGeneration.current) return
      setDirectory({ status: 'error', message: rolloutErrorText(error, t, 'rolloutControls.target.listError') })
    }
  }, [t])

  useEffect(() => {
    void loadDirectory()
  }, [loadDirectory])

  // Switching the active organisation returns the page to it.
  const activeId = active?.id
  useEffect(() => {
    setSelectedTargetId(null)
  }, [activeId])

  const listed = useMemo(
    () => (directory.status === 'ready' ? directory.list.organizations : []),
    [directory],
  )
  const listedTarget = selectedTargetId ? listed.find((organization) => organization.id === selectedTargetId) : undefined
  const crossOrganization = active !== null && active !== undefined && listedTarget !== undefined && listedTarget.id !== active.id
  const target = crossOrganization && listedTarget
    ? { id: listedTarget.id, name: listedTarget.name }
    : active ? { id: active.id, name: active.name } : null

  const options = useMemo<ComboboxOption[]>(() => {
    if (!active) return []
    const describe = (organization: StaffRolloutOrganization | undefined) => organization
      ? t('rolloutControls.target.optionLevels', {
        uiVariant: t(`rolloutControls.uiVariant.levels.${organization.uiVariant.level}.label`, { defaultValue: organization.uiVariant.level }),
        missionStack: t(`rolloutControls.missionStack.levels.${organization.missionStack.level}.label`, { defaultValue: organization.missionStack.level }),
        managedChatAddress: t(`rolloutControls.managedChat.levels.${organization.managedChatAddress.level}.label`, {
          defaultValue: organization.managedChatAddress.level,
        }),
      })
      : undefined
    const others = listed
      .filter((organization) => organization.id !== active.id)
      .sort((left, right) => left.name.localeCompare(right.name))
      .map((organization) => ({ value: organization.id, label: organization.name, description: describe(organization) }))
    return [
      {
        value: active.id,
        label: active.name,
        badge: t('rolloutControls.target.activeBadge'),
        description: describe(listed.find((organization) => organization.id === active.id)),
      },
      ...others,
    ]
  }, [active, listed, t])

  const controls = rolloutControlsFor(crossOrganization)

  return (
    <div className="container-standard">
      <PageHeader
        icon={SlidersHorizontal}
        title={t('rolloutControls.title')}
        description={t('rolloutControls.description')}
        actions={<Badge variant="purple">{t('rolloutControls.internalOnly')}</Badge>}
      />

      {isLoading ? (
        <div className="h-40 animate-pulse rounded-lg bg-slate-100 dark:bg-slate-800" aria-label={t('rolloutControls.loading')} />
      ) : !active || !target ? (
        <Alert variant="warning">{t('rolloutControls.organizationMissing')}</Alert>
      ) : (
        <div className="space-y-6">
          <div
            role="group"
            aria-labelledby={targetPickerLabelID}
            className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-800/40"
          >
            <div id={targetPickerLabelID} className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              {t('rolloutControls.target.label')}
            </div>
            <Combobox
              options={options}
              value={target.id}
              onValueChange={(value) => setSelectedTargetId(value === active.id ? null : value)}
              placeholder={t('rolloutControls.target.label')}
              searchPlaceholder={t('rolloutControls.target.searchPlaceholder')}
              emptyText={t('rolloutControls.target.empty')}
              disabled={directory.status === 'loading'}
              triggerClassName="w-full sm:max-w-md"
            />
            {directory.status === 'loading' && (
              <p className="text-xs text-slate-500 dark:text-slate-400">{t('rolloutControls.target.loading')}</p>
            )}
            {directory.status === 'ready' && directory.list.more && (
              <p className="text-xs text-amber-700 dark:text-amber-300" data-testid="rollout-target-truncated">
                {t('rolloutControls.target.truncated', { count: directory.list.organizations.length })}
              </p>
            )}
            {directory.status === 'error' && (
              <Alert variant="warning">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <span>{directory.message}</span>
                  <Button variant="secondary" size="sm" onClick={() => void loadDirectory()}>
                    <RefreshCw className="mr-1 h-4 w-4" />{t('rolloutControls.refresh')}
                  </Button>
                </div>
              </Alert>
            )}
          </div>

          {crossOrganization && (
            <Alert variant="info">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <span className="min-w-0 flex-1">
                  {t('rolloutControls.target.crossOrganizationNotice', { organization: target.name, active: active.name })}
                </span>
                <Button variant="secondary" size="sm" onClick={() => setSelectedTargetId(null)}>
                  {t('rolloutControls.target.backToActive', { organization: active.name })}
                </Button>
              </div>
            </Alert>
          )}

          {controls.map((control) => (
            <control.Component
              key={`${control.id}:${target.id}`}
              organization={target}
              activeOrganizationId={active.id}
            />
          ))}
        </div>
      )}
    </div>
  )
}
